import { useState, type FormEvent } from 'react';

const SUBMIT_URL = 'https://readdy.ai/api/form/dah2qkbp14a1h3mr6b9g';

type Status = 'idle' | 'submitting' | 'success' | 'error';

export default function NewsletterForm() {
  const [status, setStatus] = useState<Status>('idle');
  const [message, setMessage] = useState('');

  const handleSubmit = async (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const form = event.currentTarget;
    const formData = new FormData(form);

    const honeypot = String(formData.get('website_alt') || '').trim();
    if (honeypot) {
      setStatus('success');
      setMessage('订阅成功，首发提醒会第一时间发到你的邮箱。');
      form.reset();
      return;
    }

    setStatus('submitting');
    setMessage('');

    const payload = new URLSearchParams();
    formData.forEach((value, key) => {
      if (key === 'website_alt') return;
      payload.append(key, String(value));
    });

    try {
      const response = await fetch(SUBMIT_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: payload.toString(),
      });
      const responseText = await response.text();
      let parsed: { code?: string; meta?: { message?: string; detail?: string }; message?: string } | null = null;
      try {
        parsed = JSON.parse(responseText);
      } catch {
        parsed = null;
      }
      const serverMsg =
        parsed?.meta?.message || parsed?.message || parsed?.meta?.detail || responseText || '';
      const failed =
        !response.ok ||
        (parsed?.code ? parsed.code !== 'OK' : false) ||
        /spam/i.test(String(serverMsg));

      if (failed) {
        setStatus('error');
        setMessage(serverMsg || '提交失败，请稍后重试。');
        return;
      }

      setStatus('success');
      setMessage('订阅成功，首发提醒会第一时间发到你的邮箱。');
      form.reset();
    } catch {
      setStatus('error');
      setMessage('网络异常，提交失败，请稍后重试。');
    }
  };

  return (
    <form
      data-readdy-form
      id="newsletter-form"
      onSubmit={handleSubmit}
      className="w-full"
      aria-label="新瓜速递邮件订阅"
    >
      <div className="flex flex-col gap-2.5 sm:flex-row">
        <div className="relative flex-1">
          <i className="ri-mail-line pointer-events-none absolute left-3.5 top-1/2 -translate-y-1/2 text-base text-foreground-500"></i>
          <input
            type="email"
            name="email"
            required
            placeholder="留下邮箱，抢在第一手"
            className="w-full rounded-md border border-background-300/70 bg-background-50 py-2.5 pl-10 pr-3.5 text-sm text-foreground-100 placeholder:text-foreground-600 focus:border-primary-500 focus:outline-none focus:ring-1 focus:ring-primary-500/50"
          />
        </div>
        <button
          type="submit"
          disabled={status === 'submitting'}
          className="inline-flex cursor-pointer items-center justify-center gap-2 whitespace-nowrap rounded-md bg-primary-500 px-5 py-2.5 text-sm font-medium text-background-950 transition-colors hover:bg-primary-400 disabled:cursor-not-allowed disabled:opacity-60"
        >
          {status === 'submitting' ? '提交中...' : '订阅提醒'}
          <i className="ri-notification-3-line"></i>
        </button>
      </div>

      <input
        type="text"
        name="website_alt"
        className="form-ghost-field"
        tabIndex={-1}
        autoComplete="off"
        aria-hidden="true"
        readOnly
      />

      {message ? (
        <p
          className={`mt-3 text-xs ${status === 'error' ? 'text-accent-400' : 'text-secondary-400'}`}
          role="status"
        >
          {message}
        </p>
      ) : (
        <p className="mt-3 text-xs text-foreground-600">
          我们只发送首发提醒，不会用于其他用途，随时可退订。
        </p>
      )}
    </form>
  );
}